/**
 * Flags (#316): types, API calls and the Find query. A document's body says
 * which flags its lines carry (FlagExtension); the server row holds each
 * flag's state (open or addressed, the reply). See
 * docs/superpowers/specs/2026-10-27-flags-design.md.
 */
import {
  Bookmark, Calendar, CircleHelp, DollarSign, Flag, Gavel, Heart, Lightbulb, Lock, Mail, MessageCircle, Phone, Send, Star,
  TriangleAlert, User, type LucideIcon,
} from 'lucide-react';
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from './api';
import type { SourceType } from './excerpts';

export interface FlagTypeDef {
  id: string;
  label: string;
  /** How the Find sentence names several of them: "questions". */
  plural: string;
  icon: LucideIcon;
  /** A CSS colour: a theme variable for built-ins, a hex value for custom types. */
  color: string;
  addressable: boolean;
  replyLabel: string | null;
  placeholder?: string;
  custom: boolean;
  archived: boolean;
}

const builtIn = (id: string, label: string, plural: string, icon: LucideIcon, replyLabel: string | null, placeholder?: string): FlagTypeDef =>
  ({ id, label, plural, icon, color: `var(--color-flag-${id})`, addressable: replyLabel !== null, replyLabel, placeholder, custom: false, archived: false });

/** In Alt+1..7 order. */
export const BUILT_IN_FLAG_TYPES: FlagTypeDef[] = [
  builtIn('important', 'Important', 'important items', Star, 'Note'),
  builtIn('question', 'Question', 'questions', CircleHelp, 'Answer', 'e.g. 4 hours. Ken owns the runbook.'),
  builtIn('critical', 'Critical', 'critical items', TriangleAlert, 'Resolution'),
  builtIn('discuss', 'Discuss with', 'discussion points', MessageCircle, 'Outcome'),
  builtIn('idea', 'Idea', 'ideas', Lightbulb, 'Outcome', 'e.g. Parked until after cutover.'),
  builtIn('decision', 'Decision', 'decisions', Gavel, null),
  builtIn('remember', 'Remember', 'reminders', Bookmark, 'Note'),
];

/**
 * The icons and colours a type may use; the server checks the same lists. The
 * built-ins' own are included (#332) so an edited built-in can go back to them.
 */
export const CUSTOM_FLAG_ICONS: Record<string, LucideIcon> = {
  flag: Flag, user: User, mail: Mail, send: Send, lock: Lock, bookmark: Bookmark,
  phone: Phone, calendar: Calendar, 'dollar-sign': DollarSign, heart: Heart,
  star: Star, 'circle-help': CircleHelp, 'triangle-alert': TriangleAlert, 'message-circle': MessageCircle, lightbulb: Lightbulb, gavel: Gavel,
};
export const CUSTOM_FLAG_COLORS = [
  '#f97316', '#22c55e', '#38bdf8', '#e879f9', '#94a3b8', '#fb7185', '#a3e635', '#fbbf24',
  '#f59e0b', '#a78bfa', '#ef4444', '#60a5fa', '#facc15', '#2dd4bf', '#f472b6',
];

/** Each built-in's default icon and colour as picker values, for its edit form (#332). */
export const BUILT_IN_DEFAULT_LOOK: Record<string, { icon: string; color: string }> = {
  important: { icon: 'star', color: '#f59e0b' },
  question: { icon: 'circle-help', color: '#a78bfa' },
  critical: { icon: 'triangle-alert', color: '#ef4444' },
  discuss: { icon: 'message-circle', color: '#60a5fa' },
  idea: { icon: 'lightbulb', color: '#facc15' },
  decision: { icon: 'gavel', color: '#2dd4bf' },
  remember: { icon: 'bookmark', color: '#f472b6' },
};

/** "When made into a task" (#337) as stored; null means the type's default. */
export interface TaskRuleFields { task_priority: TaskPriorityRule | null; task_tag: boolean | null; task_follow_up: boolean | null }

/** A user's changes to a built-in type: the look (#332) and the task rules (#337); null means the default. */
export interface BuiltInOverride extends Partial<TaskRuleFields> { key: string; name: string | null; icon: string | null; color: string | null }

// ---- Flag-to-task rules (#337) ----
// What a task made from a flagged line gets from each flag. Mirrors
// server/src/lib/flags/taskRules.ts, which applies them.

/** 'none' is "no change": the task keeps its default priority (medium) unless another flag sets one. */
export type TaskPriorityRule = 'none' | 'low' | 'medium' | 'high' | 'urgent';
export const TASK_PRIORITY_RULES: Array<{ value: TaskPriorityRule; label: string }> = [
  { value: 'none', label: 'No change' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];
export interface TaskRule { priority: TaskPriorityRule; tag: boolean; followUp: boolean }

export const BUILT_IN_TASK_RULES: Record<string, TaskRule> = {
  important: { priority: 'high', tag: false, followUp: false },
  critical: { priority: 'urgent', tag: false, followUp: false },
  discuss: { priority: 'none', tag: false, followUp: true },
  question: { priority: 'none', tag: true, followUp: false },
  idea: { priority: 'none', tag: true, followUp: false },
  decision: { priority: 'none', tag: true, followUp: false },
  remember: { priority: 'none', tag: true, followUp: false },
};
export const CUSTOM_TASK_RULE: TaskRule = { priority: 'none', tag: true, followUp: false };

/** Types whose flags name a person, so "add a follow-up with the person" applies. */
export const PERSON_FLAG_TYPES = new Set(['discuss']);

/** A type's defaults: the built-in's, or the custom-type ones. */
export function defaultTaskRule(typeId: string): TaskRule {
  return BUILT_IN_TASK_RULES[typeId] ?? CUSTOM_TASK_RULE;
}

export const taskPriorityLabel = (p: TaskPriorityRule) => TASK_PRIORITY_RULES.find((r) => r.value === p)?.label ?? p;

/** The built-ins with a user's name, icon and colour applied; key, order and addressing never change. */
export function applyBuiltInOverrides(defs: FlagTypeDef[], overrides: BuiltInOverride[]): FlagTypeDef[] {
  const byKey = new Map(overrides.map((o) => [o.key, o]));
  return defs.map((d) => {
    const o = byKey.get(d.id);
    if (!o || d.custom) return d;
    const icon = o.icon ? CUSTOM_FLAG_ICONS[o.icon] : undefined;
    return {
      ...d,
      label: o.name ?? d.label,
      plural: o.name ? `${o.name.toLowerCase()} items` : d.plural,
      icon: icon ?? d.icon,
      color: o.color ?? d.color,
    };
  });
}

export interface FlagTypeRow {
  id: string;
  name: string;
  icon: string;
  color: string;
  addressable: boolean;
  reply_label: string;
  position: number;
  archived_at: string | null;
  task_priority: TaskPriorityRule | null;
  task_tag: boolean | null;
  task_follow_up: boolean | null;
}

export function customToDef(r: Omit<FlagTypeRow, keyof TaskRuleFields>): FlagTypeDef {
  return {
    id: r.id, label: r.name, plural: `${r.name.toLowerCase()} items`, icon: CUSTOM_FLAG_ICONS[r.icon] ?? Flag,
    color: r.color, addressable: r.addressable, replyLabel: r.addressable ? r.reply_label : null, custom: true, archived: !!r.archived_at,
  };
}

/** The type with this id, or a generic flag for one that was deleted or never existed. */
export function flagTypeById(defs: FlagTypeDef[], id: string): FlagTypeDef {
  return defs.find((d) => d.id === id)
    ?? { id, label: 'Flag', plural: 'flags', icon: Flag, color: 'var(--color-text-secondary)', addressable: true, replyLabel: 'Note', custom: true, archived: false };
}

/** The types Alt+1..9 reach, in order: built-ins, then unarchived custom types. */
export function shortcutTypes(defs: FlagTypeDef[]): FlagTypeDef[] {
  return defs.filter((d) => !d.archived).slice(0, 9);
}

// ---- API ----

export interface FlagState {
  id: string;
  type: string;
  person_id: string | null;
  status: 'open' | 'addressed';
  reply: string | null;
  addressed_at: string | null;
  task_id: string | null;
}

export interface FlagListItem extends FlagState {
  person_name: string | null;
  text: string;
  source_type: SourceType;
  source_id: string;
  source_title: string;
  flag_date: string;
  project_names: string[];
  series_id: string | null;
  series_name: string | null;
  created_at: string;
}

export type FlagStatusFilter = 'open' | 'addressed' | 'all';
export type FlagGroup = 'kind' | 'source' | 'none';

export interface FlagQuery {
  types: string[];
  status: FlagStatusFilter;
  projectId?: string;
  seriesId?: string;
  personId?: string;
  from?: string;
  to?: string;
  days?: number;
  sinceLastMeeting?: boolean;
  q?: string;
  sourceType?: SourceType;
  sourceId?: string;
  limit?: number;
  offset?: number;
}

export interface FindQuery extends FlagQuery {
  group: FlagGroup;
}

export const DEFAULT_FIND_QUERY: FindQuery = { types: [], status: 'open', group: 'kind' };

export function flagQueryParams(q: FlagQuery): string {
  const p = new URLSearchParams();
  if (q.types.length) p.set('types', q.types.join(','));
  p.set('status', q.status);
  const pairs: Array<[string, string | number | undefined]> = [
    ['project_id', q.projectId], ['series_id', q.seriesId], ['person_id', q.personId], ['from', q.from], ['to', q.to],
    ['days', q.days], ['q', q.q], ['source_type', q.sourceType], ['source_id', q.sourceId], ['limit', q.limit], ['offset', q.offset],
  ];
  for (const [k, v] of pairs) if (v !== undefined && v !== '') p.set(k, String(v));
  if (q.sinceLastMeeting) p.set('since_last_meeting', '1');
  return p.toString();
}

export const fetchFlagStates = (type: SourceType, id: string) =>
  apiGet<FlagState[]>(`/flags/by-source/${type}/${encodeURIComponent(id)}`);
export const listFlags = (q: FlagQuery) => apiGet<{ items: FlagListItem[]; total: number }>(`/flags?${flagQueryParams(q)}`);
export const patchFlag = (id: string, body: { status?: 'open' | 'addressed'; reply?: string | null }) =>
  apiPatch<FlagState>(`/flags/${encodeURIComponent(id)}`, body);
export const makeTaskFromFlag = (id: string) => apiPost<{ task_id: string; created: boolean }>(`/flags/${encodeURIComponent(id)}/task`, {});

export const fetchFlagTypes = () => apiGet<FlagTypeRow[]>('/flag-types');
export const fetchBuiltInOverrides = () => apiGet<BuiltInOverride[]>('/flag-types/built-ins');
export const saveBuiltInOverride = (key: string, look: { name?: string | null; icon?: string | null; color?: string | null } & Partial<TaskRuleFields>) =>
  apiPut<BuiltInOverride>(`/flag-types/built-ins/${encodeURIComponent(key)}`, look);
export const resetBuiltInOverride = (key: string) => apiDelete(`/flag-types/built-ins/${encodeURIComponent(key)}`);
export type FlagTypeInput = { name: string; icon: string; color: string; addressable: boolean; reply_label: string } & Partial<TaskRuleFields>;
export const createFlagType = (input: FlagTypeInput) => apiPost<FlagTypeRow>('/flag-types', input);
export const updateFlagType = (id: string, patch: Partial<FlagTypeInput & { position: number; archived: boolean }>) =>
  apiPatch<FlagTypeRow>(`/flag-types/${encodeURIComponent(id)}`, patch);
export const deleteFlagType = (id: string) => apiDelete(`/flag-types/${encodeURIComponent(id)}`);

// ---- Find query in the URL, and in words ----

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const URL_KEYS: Array<[keyof FindQuery, string]> = [
  ['projectId', 'project'], ['seriesId', 'series'], ['personId', 'person'], ['q', 'q'],
];

/** Only what differs from the default goes in the URL. */
export function queryToSearchParams(q: FindQuery): URLSearchParams {
  const p = new URLSearchParams();
  if (q.types.length) p.set('types', q.types.join(','));
  if (q.status !== 'open') p.set('status', q.status);
  if (q.group !== 'kind') p.set('group', q.group);
  for (const [k, name] of URL_KEYS) if (q[k]) p.set(name, String(q[k]));
  if (q.from) p.set('from', q.from);
  if (q.to) p.set('to', q.to);
  return p;
}

export function searchParamsToQuery(p: URLSearchParams): FindQuery {
  const q: FindQuery = { ...DEFAULT_FIND_QUERY, types: (p.get('types') ?? '').split(',').filter(Boolean) };
  const status = p.get('status');
  if (status === 'addressed' || status === 'all') q.status = status;
  const group = p.get('group');
  if (group === 'source' || group === 'none') q.group = group;
  for (const [k, name] of URL_KEYS) { const v = p.get(name); if (v) (q as unknown as Record<string, string>)[k] = v; }
  for (const k of ['from', 'to'] as const) { const v = p.get(k); if (v && DATE.test(v)) q[k] = v; }
  return q;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const shortDate = (d: string) => `${MONTHS[Number(d.slice(5, 7)) - 1]} ${Number(d.slice(8, 10))}`;

type Part = { text: string; strong: boolean };

/** "Showing open questions in Claims Portal Migration from Sep 1 to Oct 27", as plain and bold runs. */
export function describeQuery(q: FlagQuery, names: { project?: string; series?: string; person?: string }, defs: FlagTypeDef[]): Part[] {
  const parts: Part[] = [];
  const plain = (text: string) => parts.push({ text, strong: false });
  const bold = (text: string) => parts.push({ text, strong: true });

  plain('Showing ');
  if (q.types.length === 0) {
    bold(q.status === 'all' ? 'all flags' : `${q.status} flags`);
  } else {
    if (q.status !== 'all') { bold(q.status); plain(' '); }
    const words = q.types.map((t) => flagTypeById(defs, t).plural);
    bold(words.length > 1 ? `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}` : words[0]);
  }
  if (q.personId) { plain(' to discuss with '); bold(names.person ?? 'someone'); }
  plain(' in ');
  if (q.projectId && q.seriesId) { bold(names.project ?? 'a project'); plain(' / '); bold(names.series ?? 'a series'); }
  else if (q.projectId) bold(names.project ?? 'a project');
  else if (q.seriesId) bold(names.series ?? 'a series');
  else plain('any project');
  if (q.from && q.to) { plain(' from '); bold(shortDate(q.from)); plain(' to '); bold(shortDate(q.to)); }
  else if (q.from) { plain(' since '); bold(shortDate(q.from)); }
  else if (q.to) { plain(' up to '); bold(shortDate(q.to)); }
  if (q.q) { plain(' containing '); bold(`"${q.q}"`); }
  return parts;
}
